'use client';

/**
 * EqualizerBars — small audio-level glyph (voice/chat mode toggles,
 * "listening" affordances).
 *
 * Level-reactive when both `animate` and `levelRef` are given (call-screen's
 * AGENT-mode usage): a small rAF loop reads the shared audio level engine
 * ref (see useAudioVisualization.ts) each frame and writes a CSS custom
 * property (`--eq-level`) directly on the container — no React state, so
 * this never re-renders on the audio clock. Each bar's height is
 * `baseline * --eq-level`, floored so bars never collapse to a sliver
 * during a natural speech pause (the engine's level ref genuinely dips near
 * 0 between phonemes) — combined with the always-on scaleY keyframe pulse
 * below, the bars keep visibly moving even when momentarily silent. Without
 * `levelRef` (or with `animate` off), `--eq-level` is simply never set and
 * `var(--eq-level, 1)` falls back to the original static-height look.
 * "Sara is speaking" text stays mode-driven (see call-screen.tsx) — this
 * glyph doesn't own that copy.
 *
 * Turn 13 (13a "Sara's speaking") adds `heights`/`colors`/`barWidth`
 * overrides for the exact 4-bar multi-tone set the status-cluster/top-bar
 * glyphs need (3px @ desktop, ~2.5px mini @ mobile) — distinct geometry from
 * the two existing MD/SM presets below, which stay untouched so nothing
 * changes for any call site that doesn't pass the new props (there are only
 * two consumers today, call-status-cluster.tsx and call-top-bar-status.tsx,
 * both of which now opt into the override).
 */
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { AudioLevels } from '@/hooks/useAudioVisualization';

export const EQUALIZER_SIZE = {
  MD: 'md',
  SM: 'sm',
} as const;
export type EqualizerSize =
  (typeof EQUALIZER_SIZE)[keyof typeof EQUALIZER_SIZE];

const HEIGHTS: Record<EqualizerSize, number[]> = {
  [EQUALIZER_SIZE.MD]: [7, 14, 10, 16, 8],
  [EQUALIZER_SIZE.SM]: [6, 12, 9, 13],
};

const COLORS = ['#FF8E4D', '#FF7E3E', '#FFAE5C', '#FF7E3E', '#FFC781'];

/** Turn 13's 4-bar AGENT palette (13a) — the first four of the array above,
 *  called out explicitly so call sites reference a stable named export
 *  instead of re-typing the hex order (13a: "#FF8E4D / #FF7E3E / #FFAE5C /
 *  #FF7E3E"). */
export const EQUALIZER_AGENT_COLORS = COLORS.slice(0, 4);

/** Floor fraction of each bar's baseline height that always stays visible —
 *  0 would let bars vanish to a hairline during a quiet-but-still-speaking
 *  gap; 1 would make them perfectly static. 0.5 keeps them alive without
 *  drowning out the level reaction. */
const LEVEL_FLOOR = 0.5;

export interface EqualizerBarsProps {
  size?: EqualizerSize;
  /** Pulses the bars via a staggered scaleY animation, and (when `levelRef`
   *  is also given) drives their height from the live audio level. Defaults
   *  true. */
  animate?: boolean;
  /** Shared audio level engine ref (see useAudioVisualization.ts) — reads
   *  `.agent` each frame. Omit to get the plain constant-heights + pulse
   *  look (unchanged from before this glyph became level-reactive). */
  levelRef?: React.RefObject<AudioLevels>;
  /** Override the `size` preset's baseline heights (px) — Turn 13's 4-bar
   *  set needs an exact bar count/shape distinct from MD/SM. Length drives
   *  the bar COUNT (colors/animation delays index off this array). */
  heights?: number[];
  /** Override the `size` preset's colors — paired 1:1 with `heights`. */
  colors?: string[];
  /** Override the 3px default bar width (Turn 13's mobile mini scale is
   *  2.5px — see call-top-bar-status.tsx). */
  barWidth?: number;
  className?: string;
}

export function EqualizerBars({
  size = EQUALIZER_SIZE.MD,
  animate = true,
  levelRef,
  heights: heightsOverride,
  colors: colorsOverride,
  barWidth = 3,
  className,
}: EqualizerBarsProps) {
  const heights = heightsOverride ?? HEIGHTS[size];
  const colors = colorsOverride ?? COLORS;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!animate || !levelRef) return;

    let raf = 0;
    const tick = () => {
      const level = Math.max(0, Math.min(1, levelRef.current?.agent ?? 0));
      containerRef.current?.style.setProperty(
        '--eq-level',
        String(LEVEL_FLOOR + (1 - LEVEL_FLOOR) * level)
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, levelRef]);

  return (
    <div
      ref={containerRef}
      className={cn('flex items-end gap-[3px]', className)}
      aria-hidden="true"
    >
      {heights.map((height, i) => (
        <span
          key={i}
          className={cn('rounded-[2px]', animate && 'equalizer-bar')}
          style={{
            width: barWidth,
            height: `calc(${height}px * var(--eq-level, 1))`,
            backgroundColor: colors[i],
            animationDelay: animate ? `${i * 0.12}s` : undefined,
          }}
        />
      ))}
      {animate && (
        <style jsx>{`
          .equalizer-bar {
            transform-origin: bottom;
            animation: equalizerPulse 0.9s ease-in-out infinite;
          }
          @keyframes equalizerPulse {
            0%,
            100% {
              transform: scaleY(0.55);
            }
            50% {
              transform: scaleY(1);
            }
          }
        `}</style>
      )}
    </div>
  );
}
